import { useDispatch, useSelector } from "react-redux";
import { DemoOrganization } from "../../Common/Interfaces/Entities";
import { ApplicationsState } from "../../Redux/Applications/Interface";
import { AppDispatch, RootState } from "../../Redux/Store";
import { STATIC_URL } from "../../Common/constants";
import { useState } from "react";
import { MdModeEditOutline } from "react-icons/md";
import { AiFillEye } from "react-icons/ai";
import { OrganizationActions } from "../../Redux/Organizations/Actions";

type Props = DemoOrganization;

const DemoOrganizationCard: React.FC<Props> = ({
  id,
  application_id,
  name,
  devices,
  num_devices,
  code,
  banner_left,   // assume this is an existing URL/path string from server
  banner_right,  // same
  is_mig_enabled,
  is_tig_enabled,
  is_stick_enabled,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [mode, setMode] = useState<"view" | "edit">("view");

  // Keep URLs and files separate; keep booleans as booleans.
  const [updated, setUpdated] = useState({
    name,
    num_devices,
    code: code ?? "",
    is_mig_enabled: Boolean(is_mig_enabled),
    is_tig_enabled: Boolean(is_tig_enabled),
    is_stick_enabled: Boolean(is_stick_enabled),
    existingBannerLeft: banner_left ?? "",
    existingBannerRight: banner_right ?? "",
    bannerLeftFile: null as File | null,
    bannerRightFile: null as File | null,
  });

  const ApplicationsReducer: ApplicationsState = useSelector(
    (state: RootState) => state.apps
  );
  const app = ApplicationsReducer.application?.applications?.[application_id];

  const handleSubmit: React.FormEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append("id", String(id));
    data.append("name", updated.name);
    data.append("num_devices", String(updated.num_devices));
    data.append("code", updated.code);
    data.append("is_mig_enabled", updated.is_mig_enabled ? "1" : "0");
    data.append("is_tig_enabled", updated.is_tig_enabled ? "1" : "0");
    data.append("is_stick_enabled", updated.is_stick_enabled ? "1" : "0");

    // Only append files if user selected a new one; otherwise the backend should keep existing.
    if (updated.bannerLeftFile) {
      data.append("banner_left", updated.bannerLeftFile);
    }
    if (updated.bannerRightFile) {
      data.append("banner_right", updated.bannerRightFile);
    }

    dispatch(OrganizationActions.updateDemoOrganization({ organization: data }));
    setMode("view");
  };

  return mode === "view" ? (
    <div className="bg-white shadow rounded-md pb-2 overflow-hidden">
      <h1 className="p-2 bg-cythero-primary font-bold text-xl text-white mb-2 flex items-center justify-between">
        {name}
        <MdModeEditOutline className="cursor-pointer" onClick={() => setMode("edit")} />
      </h1>

      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">
        Available Devices: {num_devices}
      </h3>
      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">
        Used Devices: {devices.length}
      </h3>
      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">Code: {updated.code}</h3>
      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">
        Mig Enabled: {updated.is_mig_enabled ? "Yes" : "No"}
      </h3>
      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">
        Tig Enabled: {updated.is_tig_enabled ? "Yes" : "No"}
      </h3>
      <h3 className="px-2 font-bold text-md text-slate-800 mb-4">
        Stick Enabled: {updated.is_stick_enabled ? "Yes" : "No"}
      </h3>

      <h3 className="px-2 font-bold text-md text-slate-800 mb-1">Application</h3>
      {app && (
        <span className="ml-4 flex items-center gap-2">
          <img src={STATIC_URL + app.logo_path} className="w-8" />
          <h3 className="font-bold text-lg text-slate-800">{app.name}</h3>
        </span>
      )}
    </div>
  ) : (
    <div className="bg-white shadow rounded-md pb-2 overflow-hidden">
      <h1 className="p-2 bg-cythero-primary font-bold text-xl text-white mb-2 flex items-center justify-between">
        {name}
        <AiFillEye className="cursor-pointer" onClick={() => setMode("view")} />
      </h1>

      <div className="px-2">
        <label className="font-bold text-slate-800 text-sm">Organization Name</label>
        <input
          className="mb-2 border border-slate-400 w-full rounded-md px-2 py-1"
          type="text"
          value={updated.name}
          onChange={(e) => setUpdated({ ...updated, name: e.target.value })}
        />

        <label className="font-bold text-slate-800 text-sm">Number of seats (devices)</label>
        <input
          className="mb-2 border border-slate-400 w-full rounded-md px-2 py-1"
          type="number"
          value={updated.num_devices}
          onChange={(e) =>
            setUpdated({ ...updated, num_devices: parseInt(e.target.value || "0", 10) })
          }
        />

        <label className="font-bold text-slate-800 text-sm">Code</label>
        <input
          className="mb-2 border border-slate-400 w-full rounded-md px-2 py-1"
          type="text"
          value={updated.code}
          onChange={(e) => setUpdated({ ...updated, code: e.target.value })}
        />

        <div className="flex items-center gap-2">
          <input
            className="border border-slate-400 rounded-md"
            type="checkbox"
            checked={updated.is_mig_enabled}
            onChange={(e) => setUpdated({ ...updated, is_mig_enabled: e.target.checked })}
          />
          <label className="font-bold text-slate-800 text-sm">Mig Enabled</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            className="border border-slate-400 rounded-md"
            type="checkbox"
            checked={updated.is_tig_enabled}
            onChange={(e) => setUpdated({ ...updated, is_tig_enabled: e.target.checked })}
          />
          <label className="font-bold text-slate-800 text-sm">Tig Enabled</label>
        </div>

        <div className="flex items-center gap-2">
          <input
            className="border border-slate-400 rounded-md"
            type="checkbox"
            checked={updated.is_stick_enabled}
            onChange={(e) => setUpdated({ ...updated, is_stick_enabled: e.target.checked })}
          />
          <label className="font-bold text-slate-800 text-sm">Stick Enabled</label>
        </div>

        <label className="font-bold text-slate-800 text-sm">Banner Left</label>
        <input
          className="mb-2 w-full rounded-md px-2 py-1"
          type="file"
          // no value prop on file inputs!
          accept="image/*"
          onChange={(e) =>
            setUpdated({
              ...updated,
              bannerLeftFile: e.target.files?.[0] ?? null,
            })
          }
        />

        <label className="font-bold text-slate-800 text-sm">Banner Right</label>
        <input
          className="mb-2 w-full rounded-md px-2 py-1"
          type="file"
          accept="image/*"
          onChange={(e) =>
            setUpdated({
              ...updated,
              bannerRightFile: e.target.files?.[0] ?? null,
            })
          }
        />

        <button
          onClick={handleSubmit}
          className="block ml-auto bg-cythero-primary text-white font-bold px-2 py-1 rounded-md"
        >
          Update
        </button>
      </div>
    </div>
  );
};

export default DemoOrganizationCard;
